import React, { useState, useEffect, useRef } from "react";
import {
  ShieldCheck,
  Lock,
  KeyRound,
  Zap,
  Smartphone,
  Wallet,
  Radio,
  X,
  Globe,
  Sparkles,
  ArrowRight,
  Eye,
  EyeOff,
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext.js";
import { Language } from "../i18n/types.js";
import { useMaintenanceGate } from "../hooks/useMaintenanceGate.js";
import { BrandLogo } from "../components/common/BrandLogo.js";

export interface UnderConstructionProps {
  onUnlock?: () => void;
}

export const UnderConstruction: React.FC<UnderConstructionProps> = ({ onUnlock }) => {
  const { t, language, setLanguage, supportedLanguages } = useI18n();
  const { unlock } = useMaintenanceGate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus passcode input when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setError(null);
      setPasscode("");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen]);

  const handleUnlockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!passcode.trim()) return;

    setIsSubmitting(true);
    setError(null);

    // Attempt unlock
    const success = unlock(passcode.trim());
    if (success) {
      setIsModalOpen(false);
      onUnlock?.();
      try {
        if (typeof window !== "undefined") {
          window.location.reload();
        }
      } catch {}
    } else {
      setError(
        t.maintenance?.invalidPasscode ||
          "Invalid passkey. Please check credentials and try again."
      );
      setIsSubmitting(false);
    }
  };

  // Keyboard shortcut (Ctrl + Shift + D) to trigger dev modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        setIsModalOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col justify-between selection:bg-emerald-500 selection:text-black font-sans relative overflow-hidden">
      {/* Background Ambient Glow Gradients */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />
      <div className="absolute bottom-10 right-1/4 w-[30rem] h-[30rem] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none translate-y-1/3" />
      <div className="absolute inset-0 bg-[radial-gradient(#18181b_1px,transparent_1px)] [background-size:24px_24px] opacity-40 pointer-events-none" />

      {/* Top Header Bar */}
      <header className="relative z-10 max-w-7xl w-full mx-auto px-6 py-6 flex items-center justify-between border-b border-zinc-800/60 backdrop-blur-md">
        <div className="flex items-center space-x-3">
          <BrandLogo
            variant="full"
            size="md"
            badgeText="Alpha"
            badgeVariant="emerald"
          />
          <div className="hidden sm:block border-l border-zinc-800 pl-3">
            <p className="text-xs text-zinc-400">
              Cloudflare Stream &bull; Mobile Money &bull; Kinshasa &bull; Goma
            </p>
          </div>
        </div>

        {/* Language Switcher */}
        <div className="flex items-center space-x-1 sm:space-x-2 bg-zinc-900/80 p-1 rounded-xl border border-zinc-800 backdrop-blur-sm">
          <Globe className="w-3.5 h-3.5 text-zinc-400 ml-2 hidden sm:inline" />
          {supportedLanguages.map((lang) => {
            const isActive = language === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code as Language)}
                className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-all duration-200 ${
                  isActive
                    ? "bg-emerald-500 text-black shadow-sm font-semibold"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/80"
                }`}
              >
                {lang.name}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Hero & Content Section */}
      <main className="relative z-10 max-w-5xl w-full mx-auto px-6 py-12 flex-1 flex flex-col justify-center items-center text-center">
        {/* Pulsing Status Pill */}
        <div className="inline-flex items-center space-x-2.5 px-4 py-1.5 rounded-full bg-emerald-950/60 border border-emerald-500/30 text-emerald-300 text-xs font-medium mb-8 backdrop-blur-md shadow-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="font-mono uppercase tracking-wider text-[11px]">
            {t.maintenance?.badge || "Plateforme en Construction"}
          </span>
        </div>

        {/* Hero Title */}
        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white max-w-4xl leading-[1.15] mb-6">
          {t.maintenance?.headline || "HP-Helix Video Streaming | Bientôt Disponible"}
        </h1>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed mb-12">
          {t.maintenance?.subtitle ||
            "La plateforme vidéo pour la RDC et l'Afrique régionale est actuellement en phase de test privé."}
        </p>

        {/* 4 Feature Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full text-left mb-8 max-w-4xl">
          {/* Feature 1: 4K HLS */}
          <div className="p-5 rounded-2xl bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group backdrop-blur-sm">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
              <Zap className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white mb-1.5">
              {t.maintenance?.featureHlsTitle || "Diffusion Ultra-Basse Latence 4K"}
            </h3>
            <p className="text-sm text-zinc-400 leading-normal">
              {t.maintenance?.featureHlsDesc ||
                "Ingestion Edge CDN Cloudflare Stream directe sans transit binaire sur le serveur Fastify."}
            </p>
          </div>

          {/* Feature 2: Data Saver */}
          <div className="p-5 rounded-2xl bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group backdrop-blur-sm">
            <div className="h-10 w-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
              <Smartphone className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white mb-1.5">
              {t.maintenance?.featureMobileTitle || "Économiseur de Données Mobile"}
            </h3>
            <p className="text-sm text-zinc-400 leading-normal">
              {t.maintenance?.featureMobileDesc ||
                "Rendu optimisé pour les réseaux de RDC avec mise en cache hors-ligne et sélecteur de bande passante."}
            </p>
          </div>

          {/* Feature 3: Direct Monetization */}
          <div className="p-5 rounded-2xl bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group backdrop-blur-sm">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
              <Wallet className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white mb-1.5">
              {t.maintenance?.featureMonetizationTitle || "Monétisation Créateurs Directe"}
            </h3>
            <p className="text-sm text-zinc-400 leading-normal">
              {t.maintenance?.featureMonetizationDesc ||
                "Paiements instantanés intégrés Mobile Money (M-Pesa, Airtel Money, Orange Money) en Francs Congolais et USD."}
            </p>
          </div>

          {/* Feature 4: Live Premieres */}
          <div className="p-5 rounded-2xl bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group backdrop-blur-sm">
            <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
              <Radio className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white mb-1.5">
              {t.maintenance?.featureChatTitle || "Avant-Premières Synchronisées"}
            </h3>
            <p className="text-sm text-zinc-400 leading-normal">
              {t.maintenance?.featureChatDesc ||
                "Compte à rebours temps-réel synchronisé et flux de chat communautaire instantané."}
            </p>
          </div>
        </div>
      </main>

      {/* Footer Bar with Discrete Early Access Button */}
      <footer className="relative z-10 max-w-7xl w-full mx-auto px-6 py-6 border-t border-zinc-900/80 flex flex-col sm:flex-row items-center justify-between text-xs text-zinc-500 gap-4">
        <div>
          &copy; {new Date().getFullYear()} HP - Helix. All rights reserved.
        </div>

        {/* Discrete Developer Bypass Button */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900 transition-all border border-transparent hover:border-zinc-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          title="Developer Passkey Bypass (Ctrl+Shift+D)"
        >
          <Lock className="w-3.5 h-3.5 text-zinc-500" />
          <span>{t.maintenance?.devAccess || "Accès Développeur / Creator Early Access"}</span>
        </button>
      </footer>

      {/* Developer Passcode Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
          <div
            className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl relative text-left"
            role="dialog"
            aria-modal="true"
          >
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Modal Icon & Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white tracking-tight">
                  {t.maintenance?.devModalTitle || "Accès Développeur & Créateur"}
                </h3>
                <p className="text-xs text-zinc-400">
                  {t.maintenance?.devModalSubtitle ||
                    "Entrez le code d'accès alpha pour déverrouiller la plateforme."}
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleUnlockSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-zinc-300 mb-1.5">
                  Alpha Passkey
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    type={showPassword ? "text" : "password"}
                    value={passcode}
                    onChange={(e) => {
                      setPasscode(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder={
                      t.maintenance?.passcodePlaceholder ||
                      "Entrez la clé d'accès (ex: helix_alpha_2026)..."
                    }
                    className="w-full px-3.5 py-2.5 bg-zinc-950 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors pr-10 font-mono"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 p-1"
                    aria-label={showPassword ? "Hide passcode" : "Show passcode"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {error && (
                  <p className="mt-2 text-xs text-red-400 flex items-center space-x-1 animate-in fade-in duration-150">
                    <span>&bull;</span>
                    <span>{error}</span>
                  </p>
                )}
              </div>

              <div className="pt-2 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-xl transition-colors"
                >
                  {t.common?.cancel || "Annuler"}
                </button>
                <button
                  type="submit"
                  disabled={!passcode.trim() || isSubmitting}
                  className="px-4 py-2 text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black rounded-xl transition-all shadow-md shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <span>
                    {isSubmitting
                      ? t.maintenance?.unlocking || "Déverrouillage..."
                      : t.maintenance?.unlockButton || "Déverrouiller le Site"}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default UnderConstruction;
